import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';

import { queryClient } from '../shared/lib/query-client';
import { redirectOnSignOut } from './providers/sign-out-redirect';
import { routeTree } from '../routeTree.gen';
import { ScreenError, ScreenNotFound } from '../widgets/screen-error';
import { RealtimeProvider } from './providers/realtime-provider';
import { TooltipProvider } from '../shared/ui/tooltip';
import '../styles/main.css';

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Inside the shell's Outlet, so a crashed screen keeps the sidebar.
  defaultErrorComponent: ({ error }) => <ScreenError error={error} />,
  defaultNotFoundComponent: () => <ScreenNotFound />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

redirectOnSignOut(router);

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Must stay inside the query client: every live update ends in a cache write. */}
      <RealtimeProvider>
        {/* Radix throws without this, so a page holding one tooltip renders blank. */}
        <TooltipProvider>
          <RouterProvider router={router} />
        </TooltipProvider>
      </RealtimeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
